import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';

import { useDispatch, useSelector } from 'react-redux';
   import { fetchNewUsers } from 'coupon-common/src/modules/admin/userManager';
import { AppWrapper } from 'components';
import Breadcrumbs from 'components/BreadCrumbs/BreadCrumbs';
import { actions as redemptionActions } from 'coupon-common/src/modules/latestRedemptions';
import { actions } from 'coupon-common/src/modules/stats';
import { actions as couponActions } from 'coupon-common/src/modules/coupons';
import AnalyticsOverview from './AnalyticsCharts';
import CustomerCouponCard from 'components/CustomerCouponCard/CustomerCouponCard';

// ---------------------------------------------------------------------------
// HELPERS
// ---------------------------------------------------------------------------
// DRF paginates list responses as { count, results: [...] }.
// Unwrap to a plain array so all table components can call .sort() safely.
const unwrap = data => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.results)) return data.results;
  return [];
};

const fmt = n => (n == null ? '—' : Number(n).toLocaleString());
const fmtMs = ms => {
  if (ms == null) return '—';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};
const pct = (num, den) =>
  !den ? '—' : `${((num / den) * 100).toFixed(1)}%`;
// Same as pct() but returns a sortable numeric ratio (or -1 for "no data",
// which sinks those rows to the bottom on a descending sort).
const ratio = (num, den) => (den ? num / den : -1);

const BUCKET_ORDER = ['1', '2-3', '4-6', '7-10', '11-20', '21-40', '41+'];

// ---------------------------------------------------------------------------
// SORTING
// ---------------------------------------------------------------------------
// Generic client-side sort for a table's rows. `defaultSort` is the table's
// original comparator (e.g. day desc) and is used until the admin clicks a
// column header; after that, sortConfig takes over. A third click on the
// same header clears back to the default order.
const useSortableRows = (rows, defaultSort) => {
  const [sortConfig, setSortConfig] = useState(null); // { key, accessor, dir }

  const sorted = useMemo(() => {
    const arr = [...rows];
    if (!sortConfig) {
      if (defaultSort) arr.sort(defaultSort);
      return arr;
    }
    const { accessor, dir } = sortConfig;
    arr.sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return dir === 'asc' ? -1 : 1;
      if (av > bv) return dir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [rows, sortConfig, defaultSort]);

  const requestSort = (key, accessor) => {
    setSortConfig(prev => {
      if (!prev || prev.key !== key) return { key, accessor, dir: 'desc' };
      if (prev.dir === 'desc') return { key, accessor, dir: 'asc' };
      return null;
    });
  };

  return { rows: sorted, sortConfig, requestSort };
};

// ---------------------------------------------------------------------------
// SUB-COMPONENTS
// ---------------------------------------------------------------------------
// Foldable section wrapper used by every stat table. Collapsed by default so
// admins can scan headers/subtitles and expand only what they need.
const Collapsible = ({ title, sub, children }) => {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ marginBottom: 20 }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 8,
          width: '100%',
          textAlign: 'left',
          background: 'none',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          font: 'inherit',
        }}
      >
        <span
          style={{
            display: 'inline-block',
            marginTop: 3,
            fontSize: 10,
            color: '#888',
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.15s ease',
          }}
        >
          ▶
        </span>
        <span>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>
            {title}
          </div>
          {sub && (
            <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
              {sub}
            </div>
          )}
        </span>
      </button>
      {open && <div style={{ marginTop: 12 }}>{children}</div>}
    </div>
  );
};

const EmptyRow = ({ cols, message = 'No data yet' }) => (
  <tr>
    <td
      colSpan={cols}
      style={{
        padding: '24px 12px',
        textAlign: 'center',
        color: '#999',
        fontSize: 13,
      }}
    >
      {message}
    </td>
  </tr>
);

const LoadingRow = ({ cols }) => (
  <tr>
    <td
      colSpan={cols}
      style={{
        padding: '24px 12px',
        textAlign: 'center',
        color: '#999',
        fontSize: 13,
      }}
    >
      Loading…
    </td>
  </tr>
);

const Table = ({ children, style }) => (
  <div
    style={{
      overflowX: 'auto',
      border: '1px solid #dee2e6',
      borderRadius: 4,
      marginBottom: 8,
      ...style,
    }}
  >
    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 13,
      }}
    >
      {children}
    </table>
  </div>
);

// Column header. When `sortKey` + `onSort` are both provided, the header
// becomes clickable and shows a sort direction indicator. `accessor` lets a
// header sort by a derived value (e.g. a computed CTR) instead of a raw
// field — it defaults to reading `sortKey` straight off the row.
const Th = ({ children, right, style, sortKey, accessor, sortConfig, onSort }) => {
  const sortable = !!onSort && !!sortKey;
  const active = sortable && sortConfig && sortConfig.key === sortKey;

  return (
    <th
      onClick={
        sortable
          ? () => onSort(sortKey, accessor || (row => row[sortKey]))
          : undefined
      }
      style={{
        background: '#f8f9fa',
        padding: '8px 12px',
        textAlign: right ? 'right' : 'left',
        fontWeight: 600,
        fontSize: 11,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: active ? '#333' : '#555',
        borderBottom: '2px solid #dee2e6',
        whiteSpace: 'nowrap',
        cursor: sortable ? 'pointer' : undefined,
        userSelect: sortable ? 'none' : undefined,
        ...style,
      }}
    >
      {children}
      {sortable && (
        <span
          style={{
            marginLeft: 4,
            fontSize: 9,
            color: active ? '#007bff' : '#ccc',
          }}
        >
          {active ? (sortConfig.dir === 'asc' ? '▲' : '▼') : '⇅'}
        </span>
      )}
    </th>
  );
};

const Td = ({ children, right, muted, style }) => (
  <td
    style={{
      padding: '8px 12px',
      borderBottom: '1px solid #dee2e6',
      textAlign: right ? 'right' : 'left',
      color: muted ? '#888' : '#333',
      fontVariantNumeric: right ? 'tabular-nums' : undefined,
      verticalAlign: 'middle',
      ...style,
    }}
  >
    {children}
  </td>
);

const TabRow = ({ tabs, active, onChange }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
    {tabs.map(t => (
      <button
        key={t.value}
        onClick={() => onChange(t.value)}
        style={{
          fontSize: 12,
          padding: '4px 14px',
          borderRadius: 4,
          border: '1px solid #dee2e6',
          background: active === t.value ? '#007bff' : '#fff',
          color: active === t.value ? '#fff' : '#555',
          fontWeight: active === t.value ? 600 : 400,
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        {t.label}
      </button>
    ))}
  </div>
);

const Divider = () => (
  <hr style={{ border: 'none', borderTop: '1px solid #dee2e6', margin: '8px 0 24px' }} />
);

const BucketBadge = ({ bucket }) => {
  const colors = {
    '1':     { bg: '#E1F5EE', color: '#085041' },
    '2-3':   { bg: '#E6F1FB', color: '#0C447C' },
    '4-6':   { bg: '#EEEDFE', color: '#3C3489' },
    '7-10':  { bg: '#FAEEDA', color: '#633806' },
    '11-20': { bg: '#FAECE7', color: '#4A1B0C' },
    '21-40': { bg: '#F1EFE8', color: '#444441' },
    '41+':   { bg: '#F1EFE8', color: '#5F5E5A' },
  };
  const c = colors[bucket] || colors['41+'];
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 500,
        padding: '2px 8px',
        borderRadius: 99,
        background: c.bg,
        color: c.color,
      }}
    >
      #{bucket}
    </span>
  );
};

// Same visual language as BucketBadge, but a stable colour per screen name
// (hashed) instead of a fixed lookup table — screen names aren't a bounded
// set the way position buckets are.
const SCREEN_BADGE_PALETTE = [
  { bg: '#E1F5EE', color: '#085041' },
  { bg: '#E6F1FB', color: '#0C447C' },
  { bg: '#EEEDFE', color: '#3C3489' },
  { bg: '#FAEEDA', color: '#633806' },
  { bg: '#FAECE7', color: '#4A1B0C' },
  { bg: '#F1EFE8', color: '#444441' },
];

const ScreenBadge = ({ screen }) => {
  if (!screen) {
    return <span style={{ fontSize: 11, color: '#bbb' }}>—</span>;
  }
  let hash = 0;
  for (let i = 0; i < screen.length; i++) {
    hash = (hash * 31 + screen.charCodeAt(i)) | 0;
  }
  const c = SCREEN_BADGE_PALETTE[Math.abs(hash) % SCREEN_BADGE_PALETTE.length];
  return (
    <span
      style={{
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 500,
        padding: '2px 8px',
        borderRadius: 99,
        background: c.bg,
        color: c.color,
      }}
    >
      {screen}
    </span>
  );
};

// ---------------------------------------------------------------------------
// SECTION: Granular raw events (last 14 days)
// ---------------------------------------------------------------------------
const RawEventsTable = ({ data, loading }) => {
  const rawRows = unwrap(data);
  const { rows, sortConfig, requestSort } = useSortableRows(rawRows, null);

  return (
    <Collapsible
      title="Granular events"
      sub={`analytics_event partitions · last 14 days · expires nightly · ${rawRows.length} rows`}
    >
      <Table>
        <thead>
          <tr>
            <Th sortKey="ts" sortConfig={sortConfig} onSort={requestSort}>Timestamp</Th>
            <Th sortKey="event_type" sortConfig={sortConfig} onSort={requestSort}>Event type</Th>
            <Th sortKey="user_id" sortConfig={sortConfig} onSort={requestSort}>User ID</Th>
            <Th sortKey="coupon_id" sortConfig={sortConfig} onSort={requestSort}>Coupon ID</Th>
            <Th sortKey="business_id" sortConfig={sortConfig} onSort={requestSort}>Business ID</Th>
            <Th right sortKey="feed_position" sortConfig={sortConfig} onSort={requestSort}>Position</Th>
            <Th right sortKey="dwell_ms" sortConfig={sortConfig} onSort={requestSort}>Dwell ms</Th>
            <Th right sortKey="duration_ms" sortConfig={sortConfig} onSort={requestSort}>Duration ms</Th>
            <Th sortKey="screen" sortConfig={sortConfig} onSort={requestSort}>Screen</Th>
            <Th sortKey="os" sortConfig={sortConfig} onSort={requestSort}>OS</Th>
            <Th sortKey="device_model" sortConfig={sortConfig} onSort={requestSort}>Device model</Th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <LoadingRow cols={11} />
          ) : rows.length === 0 ? (
            <EmptyRow cols={11} message="No raw events in the last 14 days" />
          ) : (
            rows.map((e, i) => (
              <tr
                key={e.event_id || i}
                style={{ cursor: 'default' }}
                onMouseEnter={ev =>
                  (ev.currentTarget.style.background =
                    '#f8f9fa')
                }
                onMouseLeave={ev =>
                  (ev.currentTarget.style.background = 'transparent')
                }
              >
                <Td muted style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                  {e.ts ? new Date(e.ts).toLocaleString() : '—'}
                </Td>
                <Td>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 500,
                      padding: '2px 7px',
                      borderRadius: 99,
                      background: '#f8f9fa',
                      color: '#888',
                      border: '1px solid #dee2e6',
                    }}
                  >
                    {e.event_type}
                  </span>
                </Td>
                <Td muted>{e.user_id ?? '—'}</Td>
                <Td muted>{e.coupon_id ?? '—'}</Td>
                <Td muted>{e.business_id ?? '—'}</Td>
                <Td right muted>{e.feed_position ?? '—'}</Td>
                <Td right muted>{fmt(e.dwell_ms)}</Td>
                <Td right muted>{fmt(e.duration_ms)}</Td>
                <Td><ScreenBadge screen={e.screen} /></Td>
                <Td muted style={{ fontSize: 11 }}>{e.os ?? '—'}</Td>
                <Td muted style={{ fontSize: 11 }}>{e.device_model ?? '—'}</Td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </Collapsible>
  );
};

// ---------------------------------------------------------------------------
// SECTION: Coupon daily stats (long-term aggregate)
// ---------------------------------------------------------------------------
const couponDailyStatsDefaultSort = (a, b) => {
  if (a.day < b.day) return 1;
  if (a.day > b.day) return -1;
  return 0;
};

const CouponDailyStatsTable = ({ data, loading }) => {
  const rawRows = unwrap(data);
  const { rows, sortConfig, requestSort } = useSortableRows(
    rawRows,
    couponDailyStatsDefaultSort,
  );

  return (
    <Collapsible
      title="Coupon daily stats"
      sub="coupon_daily_stats · long-term aggregate · one row per coupon per day"
    >
      <Table>
        <thead>
          <tr>
            <Th sortKey="day" sortConfig={sortConfig} onSort={requestSort}>Day</Th>
            <Th sortKey="coupon_id" sortConfig={sortConfig} onSort={requestSort}>Coupon ID</Th>
            <Th right sortKey="impressions_total" sortConfig={sortConfig} onSort={requestSort}>Impressions</Th>
            <Th right sortKey="impressions_eligible" sortConfig={sortConfig} onSort={requestSort}>Eligible</Th>
            <Th right sortKey="clicks_total" sortConfig={sortConfig} onSort={requestSort}>Clicks</Th>
            <Th
              right
              sortKey="gross_ctr"
              accessor={r => ratio(r.clicks_total, r.impressions_total)}
              sortConfig={sortConfig}
              onSort={requestSort}
            >
              Gross CTR
            </Th>
            <Th right sortKey="reach_unique" sortConfig={sortConfig} onSort={requestSort}>Unique reach</Th>
            <Th right sortKey="unique_clicks" sortConfig={sortConfig} onSort={requestSort}>Unique clicks</Th>
            <Th
              right
              sortKey="reach_ctr"
              accessor={r => ratio(r.unique_clicks, r.reach_unique)}
              sortConfig={sortConfig}
              onSort={requestSort}
            >
              Reach CTR
            </Th>
            <Th right sortKey="scroll_past" sortConfig={sortConfig} onSort={requestSort}>Scroll past</Th>
            <Th right sortKey="quick_bounce" sortConfig={sortConfig} onSort={requestSort}>Quick bounce</Th>
            <Th right sortKey="new_favourites" sortConfig={sortConfig} onSort={requestSort}>New favs</Th>
            <Th right sortKey="avg_time_to_click_ms" sortConfig={sortConfig} onSort={requestSort}>Avg dwell ms</Th>
            <Th right sortKey="avg_time_on_coupon_ms" sortConfig={sortConfig} onSort={requestSort}>Avg view ms</Th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <LoadingRow cols={14} />
          ) : rows.length === 0 ? (
            <EmptyRow cols={14} />
          ) : (
            rows.map((r, i) => (
              <tr
                key={`${r.day}-${r.coupon_id}-${i}`}
                onMouseEnter={ev =>
                  (ev.currentTarget.style.background =
                    '#f8f9fa')
                }
                onMouseLeave={ev =>
                  (ev.currentTarget.style.background = 'transparent')
                }
              >
                <Td muted style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{r.day}</Td>
                <Td>{r.coupon_id}</Td>
                <Td right>{fmt(r.impressions_total)}</Td>
                <Td right muted>{fmt(r.impressions_eligible)}</Td>
                <Td right>{fmt(r.clicks_total)}</Td>
                <Td right>{pct(r.clicks_total, r.impressions_total)}</Td>
                <Td right>{fmt(r.reach_unique)}</Td>
                <Td right>{fmt(r.unique_clicks)}</Td>
                <Td right>{pct(r.unique_clicks, r.reach_unique)}</Td>
                <Td right muted>{fmt(r.scroll_past)}</Td>
                <Td right muted>{fmt(r.quick_bounce)}</Td>
                <Td right>{fmt(r.new_favourites)}</Td>
                <Td right muted>{fmt(r.avg_time_to_click_ms)}</Td>
                <Td right muted>{fmt(r.avg_time_on_coupon_ms)}</Td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </Collapsible>
  );
};

// ---------------------------------------------------------------------------
// SECTION: Position bucket breakdown
// ---------------------------------------------------------------------------
const positionBucketsDefaultSort = (a, b) => {
  if (a.day < b.day) return 1;
  if (a.day > b.day) return -1;
  const ai = BUCKET_ORDER.indexOf(a.position_bucket);
  const bi = BUCKET_ORDER.indexOf(b.position_bucket);
  return ai - bi;
};

const PositionBucketsTable = ({ data, loading }) => {
  const rawRows = unwrap(data);
  const { rows, sortConfig, requestSort } = useSortableRows(
    rawRows,
    positionBucketsDefaultSort,
  );

  return (
    <Collapsible
      title="Feed position breakdown"
      sub="coupon_daily_stats_by_pos_bucket · impressions & clicks by where coupon appeared in feed"
    >
      <Table>
        <thead>
          <tr>
            <Th sortKey="day" sortConfig={sortConfig} onSort={requestSort}>Day</Th>
            <Th sortKey="coupon_id" sortConfig={sortConfig} onSort={requestSort}>Coupon ID</Th>
            <Th
              sortKey="position_bucket"
              accessor={r => BUCKET_ORDER.indexOf(r.position_bucket)}
              sortConfig={sortConfig}
              onSort={requestSort}
            >
              Position
            </Th>
            <Th right sortKey="impressions_total" sortConfig={sortConfig} onSort={requestSort}>Impressions</Th>
            <Th right sortKey="clicks_total" sortConfig={sortConfig} onSort={requestSort}>Clicks</Th>
            <Th
              right
              sortKey="ctr"
              accessor={r => ratio(r.clicks_total, r.impressions_total)}
              sortConfig={sortConfig}
              onSort={requestSort}
            >
              CTR
            </Th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <LoadingRow cols={6} />
          ) : rows.length === 0 ? (
            <EmptyRow cols={6} />
          ) : (
            rows.map((r, i) => (
              <tr
                key={`${r.day}-${r.coupon_id}-${r.position_bucket}-${i}`}
                onMouseEnter={ev =>
                  (ev.currentTarget.style.background =
                    '#f8f9fa')
                }
                onMouseLeave={ev =>
                  (ev.currentTarget.style.background = 'transparent')
                }
              >
                <Td muted style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{r.day}</Td>
                <Td>{r.coupon_id}</Td>
                <Td><BucketBadge bucket={r.position_bucket} /></Td>
                <Td right>{fmt(r.impressions_total)}</Td>
                <Td right>{fmt(r.clicks_total)}</Td>
                <Td right>{pct(r.clicks_total, r.impressions_total)}</Td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </Collapsible>
  );
};

// ---------------------------------------------------------------------------
// SECTION: Screen breakdown
// Mirrors PositionBucketsTable, but bucketed by which screen the
// impression/click fired from (coupon_daily_stats_by_screen) instead of
// feed position. Default order is day desc, then impressions desc within a
// day so the busiest screens surface first (screen names aren't a fixed,
// orderable set the way position buckets are) — column sorting overrides
// this on click same as every other table.
// ---------------------------------------------------------------------------
const screenBreakdownDefaultSort = (a, b) => {
  if (a.day < b.day) return 1;
  if (a.day > b.day) return -1;
  return (b.impressions_total || 0) - (a.impressions_total || 0);
};

const ScreenBreakdownTable = ({ data, loading }) => {
  const rawRows = unwrap(data);
  const { rows, sortConfig, requestSort } = useSortableRows(
    rawRows,
    screenBreakdownDefaultSort,
  );

  return (
    <Collapsible
      title="Screen breakdown"
      sub="coupon_daily_stats_by_screen · impressions & clicks by which app screen the event fired from"
    >
      <Table>
        <thead>
          <tr>
            <Th sortKey="day" sortConfig={sortConfig} onSort={requestSort}>Day</Th>
            <Th sortKey="coupon_id" sortConfig={sortConfig} onSort={requestSort}>Coupon ID</Th>
            <Th sortKey="screen" sortConfig={sortConfig} onSort={requestSort}>Screen</Th>
            <Th right sortKey="impressions_total" sortConfig={sortConfig} onSort={requestSort}>Impressions</Th>
            <Th right sortKey="clicks_total" sortConfig={sortConfig} onSort={requestSort}>Clicks</Th>
            <Th
              right
              sortKey="ctr"
              accessor={r => ratio(r.clicks_total, r.impressions_total)}
              sortConfig={sortConfig}
              onSort={requestSort}
            >
              CTR
            </Th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <LoadingRow cols={6} />
          ) : rows.length === 0 ? (
            <EmptyRow cols={6} />
          ) : (
            rows.map((r, i) => (
              <tr
                key={`${r.day}-${r.coupon_id}-${r.screen}-${i}`}
                onMouseEnter={ev =>
                  (ev.currentTarget.style.background =
                    '#f8f9fa')
                }
                onMouseLeave={ev =>
                  (ev.currentTarget.style.background = 'transparent')
                }
              >
                <Td muted style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{r.day}</Td>
                <Td>{r.coupon_id}</Td>
                <Td><ScreenBadge screen={r.screen} /></Td>
                <Td right>{fmt(r.impressions_total)}</Td>
                <Td right>{fmt(r.clicks_total)}</Td>
                <Td right>{pct(r.clicks_total, r.impressions_total)}</Td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </Collapsible>
  );
};

// ---------------------------------------------------------------------------
// SECTION: Coupon ranking (windowed)
// ---------------------------------------------------------------------------
const RankingTable = ({ data, loading }) => {
  const rawRows = unwrap(data);
  // No default comparator — API already returns these ordered by gross
  // impressions, and that's exactly what the "#" column reflects until an
  // admin clicks a header.
  const { rows, sortConfig, requestSort } = useSortableRows(rawRows, null);

  return (
    <Collapsible
      title="Coupon ranking"
      sub="stats/ranking/ · windowed aggregate · ordered by gross impressions"
    >
      <Table>
        <thead>
          <tr>
            <Th style={{ width: 32 }}>#</Th>
            <Th sortKey="coupon_id" sortConfig={sortConfig} onSort={requestSort}>Coupon ID</Th>
            <Th right sortKey="impressions" sortConfig={sortConfig} onSort={requestSort}>Impressions</Th>
            <Th right sortKey="clicks" sortConfig={sortConfig} onSort={requestSort}>Clicks</Th>
            <Th right sortKey="reach_unique_window" sortConfig={sortConfig} onSort={requestSort}>Unique reach</Th>
            <Th right>Unique clicks</Th>
            <Th
              right
              sortKey="gross_ctr"
              accessor={r => ratio(r.clicks, r.impressions)}
              sortConfig={sortConfig}
              onSort={requestSort}
            >
              Gross CTR
            </Th>
            <Th
              right
              sortKey="reach_rate"
              accessor={r => ratio(r.reach_unique_window, r.impressions)}
              sortConfig={sortConfig}
              onSort={requestSort}
            >
              Reach rate
            </Th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <LoadingRow cols={8} />
          ) : rows.length === 0 ? (
            <EmptyRow cols={8} />
          ) : (
            rows.map((r, i) => (
              <tr
                key={r.coupon_id || i}
                onMouseEnter={ev =>
                  (ev.currentTarget.style.background =
                    '#f8f9fa')
                }
                onMouseLeave={ev =>
                  (ev.currentTarget.style.background = 'transparent')
                }
              >
                <Td muted style={{ fontWeight: 500 }}>{i + 1}</Td>
                <Td>{r.coupon_id}</Td>
                <Td right>{fmt(r.impressions)}</Td>
                <Td right>{fmt(r.clicks)}</Td>
                <Td right>{fmt(r.reach_unique_window)}</Td>
                <Td right>—</Td>
                <Td right>{pct(r.clicks, r.impressions)}</Td>
                <Td right>{pct(r.reach_unique_window, r.impressions)}</Td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </Collapsible>
  );
};

// ---------------------------------------------------------------------------
// SECTION: User daily stats (long-term)
// ---------------------------------------------------------------------------
const userDailyStatsDefaultSort = (a, b) => {
  if (a.day < b.day) return 1;
  if (a.day > b.day) return -1;
  return 0;
};

const UserDailyStatsTable = ({ data, loading }) => {
  const rawRows = unwrap(data);
  const { rows, sortConfig, requestSort } = useSortableRows(
    rawRows,
    userDailyStatsDefaultSort,
  );

  return (
    <Collapsible
      title="User daily stats"
      sub="user_daily_stats · long-term per-user engagement · one row per user per day"
    >
      <Table>
        <thead>
          <tr>
            <Th sortKey="day" sortConfig={sortConfig} onSort={requestSort}>Day</Th>
            <Th sortKey="user_id" sortConfig={sortConfig} onSort={requestSort}>User ID</Th>
            <Th right sortKey="sessions" sortConfig={sortConfig} onSort={requestSort}>Sessions</Th>
            <Th right sortKey="foreground_time_ms" sortConfig={sortConfig} onSort={requestSort}>Time in app</Th>
            <Th right sortKey="impressions" sortConfig={sortConfig} onSort={requestSort}>Impressions</Th>
            <Th right sortKey="clicks" sortConfig={sortConfig} onSort={requestSort}>Clicks</Th>
            <Th
              right
              sortKey="ctr"
              accessor={r => ratio(r.clicks, r.impressions)}
              sortConfig={sortConfig}
              onSort={requestSort}
            >
              CTR
            </Th>
            <Th right sortKey="coupon_views" sortConfig={sortConfig} onSort={requestSort}>Views</Th>
            <Th right sortKey="scroll_past" sortConfig={sortConfig} onSort={requestSort}>Scroll past</Th>
            <Th right sortKey="quick_bounces" sortConfig={sortConfig} onSort={requestSort}>Quick bounce</Th>
            <Th right sortKey="affiliate_clicks" sortConfig={sortConfig} onSort={requestSort}>Affiliate clicks</Th>
            <Th right sortKey="directions_clicks" sortConfig={sortConfig} onSort={requestSort}>Dir. clicks</Th>
            <Th right sortKey="call_clicks" sortConfig={sortConfig} onSort={requestSort}>Call clicks</Th>
            <Th right sortKey="profile_visits" sortConfig={sortConfig} onSort={requestSort}>Profile visits</Th>
            <Th right sortKey="distinct_coupons_viewed" sortConfig={sortConfig} onSort={requestSort}>Distinct coupons</Th>
            <Th right sortKey="distinct_businesses" sortConfig={sortConfig} onSort={requestSort}>Distinct biz</Th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <LoadingRow cols={16} />
          ) : rows.length === 0 ? (
            <EmptyRow cols={16} />
          ) : (
            rows.map((r, i) => (
              <tr
                key={`${r.day}-${r.user_id}-${i}`}
                onMouseEnter={ev =>
                  (ev.currentTarget.style.background =
                    '#f8f9fa')
                }
                onMouseLeave={ev =>
                  (ev.currentTarget.style.background = 'transparent')
                }
              >
                <Td muted style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{r.day}</Td>
                <Td>{r.user_id}</Td>
                <Td right>{fmt(r.sessions)}</Td>
                <Td right>{fmtMs(r.foreground_time_ms)}</Td>
                <Td right>{fmt(r.impressions)}</Td>
                <Td right>{fmt(r.clicks)}</Td>
                <Td right>{pct(r.clicks, r.impressions)}</Td>
                <Td right>{fmt(r.coupon_views)}</Td>
                <Td right muted>{fmt(r.scroll_past)}</Td>
                <Td right muted>{fmt(r.quick_bounces)}</Td>
                <Td right>{fmt(r.affiliate_clicks)}</Td>
                <Td right muted>{fmt(r.directions_clicks)}</Td>
                <Td right muted>{fmt(r.call_clicks)}</Td>
                <Td right muted>{fmt(r.profile_visits)}</Td>
                <Td right>{fmt(r.distinct_coupons_viewed)}</Td>
                <Td right>{fmt(r.distinct_businesses)}</Td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </Collapsible>
  );
};

// ---------------------------------------------------------------------------
// SECTION: User coupon daily stats (permanent per-user × coupon)
// ---------------------------------------------------------------------------
const userCouponStatsDefaultSort = (a, b) => {
  if (a.day < b.day) return 1;
  if (a.day > b.day) return -1;
  return b.clicks - a.clicks;
};

const UserCouponStatsTable = ({ data, loading }) => {
  const rawRows = unwrap(data);
  const { rows, sortConfig, requestSort } = useSortableRows(
    rawRows,
    userCouponStatsDefaultSort,
  );

  return (
    <Collapsible
      title="User × coupon activity"
      sub="user_coupon_daily_stats · permanent · which coupons each user interacted with"
    >
      <Table>
        <thead>
          <tr>
            <Th sortKey="day" sortConfig={sortConfig} onSort={requestSort}>Day</Th>
            <Th sortKey="user_id" sortConfig={sortConfig} onSort={requestSort}>User ID</Th>
            <Th
              sortKey="coupon"
              accessor={r => r.coupon_title || r.coupon_id || ''}
              sortConfig={sortConfig}
              onSort={requestSort}
            >
              Coupon
            </Th>
            <Th
              sortKey="business_name"
              accessor={r => r.business_name || ''}
              sortConfig={sortConfig}
              onSort={requestSort}
            >
              Business
            </Th>
            <Th right sortKey="impressions" sortConfig={sortConfig} onSort={requestSort}>Impressions</Th>
            <Th right sortKey="clicks" sortConfig={sortConfig} onSort={requestSort}>Clicks</Th>
            <Th
              right
              sortKey="ctr"
              accessor={r => ratio(r.clicks, r.impressions)}
              sortConfig={sortConfig}
              onSort={requestSort}
            >
              CTR
            </Th>
            <Th right sortKey="coupon_views" sortConfig={sortConfig} onSort={requestSort}>Views</Th>
            <Th right sortKey="scroll_past" sortConfig={sortConfig} onSort={requestSort}>Scroll past</Th>
            <Th right sortKey="quick_bounce" sortConfig={sortConfig} onSort={requestSort}>Quick bounce</Th>
            <Th right sortKey="avg_dwell_ms" sortConfig={sortConfig} onSort={requestSort}>Avg dwell ms</Th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <LoadingRow cols={11} />
          ) : rows.length === 0 ? (
            <EmptyRow cols={11} message="No data yet — run the rollup to populate" />
          ) : (
            rows.map((r, i) => (
              <tr
                key={`${r.day}-${r.user_id}-${r.coupon_id}-${i}`}
                onMouseEnter={ev =>
                  (ev.currentTarget.style.background =
                    '#f8f9fa')
                }
                onMouseLeave={ev =>
                  (ev.currentTarget.style.background = 'transparent')
                }
              >
                <Td muted style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{r.day}</Td>
                <Td muted>{r.user_id}</Td>
                <Td>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>
                    {r.coupon_title || r.coupon_id}
                  </div>
                  <div style={{ fontSize: 11, color: '#aaa' }}>
                    #{r.coupon_id}
                  </div>
                </Td>
                <Td muted style={{ fontSize: 12 }}>{r.business_name || '—'}</Td>
                <Td right>{fmt(r.impressions)}</Td>
                <Td right>{fmt(r.clicks)}</Td>
                <Td right>{pct(r.clicks, r.impressions)}</Td>
                <Td right>{fmt(r.coupon_views)}</Td>
                <Td right muted>{fmt(r.scroll_past)}</Td>
                <Td right muted>{fmt(r.quick_bounce)}</Td>
                <Td right muted>{fmt(r.avg_dwell_ms)}</Td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </Collapsible>
  );
};

// ---------------------------------------------------------------------------
// MAIN PAGE
// ---------------------------------------------------------------------------
const DAY_TABS = [
  { label: '7 days',  value: 7  },
  { label: '14 days', value: 14 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
];

// ---------------------------------------------------------------------------
// SECTION: Newest coupons (sidebar)
// ---------------------------------------------------------------------------
const NEWEST_COUPONS_LIMIT = 20;

const timeAgo = iso => {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

const discountLabel = c => {
  if (Number(c.percentage_off) > 0) return `${Number(c.percentage_off)}% off`;
  if (Number(c.amount_off) > 0) return `$${Number(c.amount_off)} off`;
  if (Number(c.points_back) > 0) return `${fmt(c.points_back)} pts back`;
  return null;
};
const SITE_URL = 'https://www.mycouponbook.ca';
const couponUrl = id => `${SITE_URL}/coupons-detail/${id}`;
const userUrl = id => `${SITE_URL}/admin/users/${id}`;
const openInNewTab = url => window.open(url, '_blank', 'noopener,noreferrer');
const noop = () => {};

// Minimum width of one coupon card when the sidebar is expanded. Raise it for
// fewer, wider cards; lower it for more columns.
const CARD_COL_MIN = 560;

// ---- shared sidebar pieces ------------------------------------------------
const SidebarMessage = ({ text, color = '#999' }) => (
  <div
    style={{
      gridColumn: '1 / -1',
      padding: '24px 12px',
      textAlign: 'center',
      color,
      fontSize: 13,
    }}
  >
    {text}
  </div>
);

// One clickable row. Collapsed: compact stacked row. Expanded: a larger tile.
const SidebarRow = ({ href, first, expanded, children }) => {
  const rest = expanded ? '#fff' : 'transparent';
  const style = {
    display: 'block',
    padding: expanded ? '16px 20px' : '8px 12px',
    fontSize: 12,
    color: 'inherit',
    textDecoration: 'none',
    cursor: href ? 'pointer' : 'default',
    minWidth: 0,
    ...(expanded
      ? { background: '#fff', border: '1px solid #e3e6ea', borderRadius: 8 }
      : { borderTop: first ? 'none' : '1px solid #f1f3f5' }),
  };
  const hover = {
    onMouseEnter: ev => { ev.currentTarget.style.background = expanded ? '#f1f5fb' : '#f8f9fa'; },
    onMouseLeave: ev => { ev.currentTarget.style.background = rest; },
  };
  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer" style={style} {...hover}>
      {children}
    </a>
  ) : (
    <div style={style} {...hover}>{children}</div>
  );
};

// `cards` = expanded coupon view (wide card grid); otherwise expanded rows
// become a grid of larger tiles that stretch to fill the page width.
const SidebarPanel = ({ title, sub, expanded, cards, colMin, children }) => (
  <div style={{ border: '1px solid #dee2e6', borderRadius: 4, overflow: 'hidden' }}>
    <div style={{ padding: expanded ? '14px 20px' : '10px 12px', borderBottom: '1px solid #dee2e6' }}>
      <div style={{ fontSize: expanded ? 18 : 14, fontWeight: 600, color: '#333' }}>{title}</div>
      <div style={{ fontSize: expanded ? 13 : 11, color: '#888', marginTop: 2 }}>{sub}</div>
    </div>
    <div style={{ maxHeight: expanded ? 'calc(100vh - 210px)' : 'calc(100vh - 160px)', overflowY: 'auto' }}>
      <div
        style={
          expanded
            ? {
                display: 'grid',
                gridTemplateColumns: `repeat(auto-fill, minmax(min(${colMin || (cards ? CARD_COL_MIN : 420)}px, 100%), 1fr))`,
                gap: cards || colMin ? 20 : 12,
                padding: 16,
                background: '#fafbfc',
              }
            : undefined
        }
      >
        {children}
      </div>
    </div>
  </div>
);

const rowTitleStyle = expanded => ({
  fontSize: expanded ? 17 : 13,
  fontWeight: 600,
  color: '#222',
  lineHeight: 1.3,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});
const rowSubStyle = expanded => ({
  color: '#555',
  fontSize: expanded ? 14 : 12,
  marginTop: expanded ? 4 : 0,
  lineHeight: 1.4,
  ...(expanded
    ? { whiteSpace: 'normal', wordBreak: 'break-word' }
    : { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
});
const rowMetaStyle = expanded => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: 8,
  color: '#777',
  fontSize: expanded ? 13 : 12,
  marginTop: expanded ? 10 : 2,
});

// ---------------------------------------------------------------------------
// Newest coupons — collapsed: compact rows. Expanded: the real coupon card.
// ---------------------------------------------------------------------------
const NewestCouponsPanel = ({ data, loading, error, expanded }) => {
  const all = unwrap(data);
  const rows = expanded ? all : all.slice(0, NEWEST_COUPONS_LIMIT);

  let body;
  if (rows.length === 0 && loading) {
    body = <SidebarMessage text="Loading…" />;
  } else if (rows.length === 0 && error) {
    body = <SidebarMessage text="Could not load newest coupons" color="#c0392b" />;
  } else if (rows.length === 0) {
    body = <SidebarMessage text="No data yet" />;
  } else if (expanded) {
    body = rows.map(c => (
      <div key={c.id} style={{ minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            margin: '0 6px 8px',
            fontSize: 13,
            color: '#777',
          }}
        >
          <span>#{c.id}</span>
          <span>Published {timeAgo(c.published_at)}</span>
        </div>
        <CustomerCouponCard
          data={c}
          layout="list"
          first
          readOnly
          isFavourite={false}
          toggleFavourite={noop}
          goToBusinessDetails={noop}
          onPress={() => c.id && openInNewTab(couponUrl(c.id))}
        />
      </div>
    ));
  } else {
    body = rows.map((c, i) => {
      const businessName = c.business && c.business.name;
      const categoryName = c.category && c.category.name;
      const discount = discountLabel(c);
      return (
        <SidebarRow key={c.id} href={c.id ? couponUrl(c.id) : null} first={i === 0}>
          <div title={c.title} style={rowTitleStyle(false)}>{c.title}</div>
          <div style={rowSubStyle(false)}>
            {[businessName, categoryName].filter(Boolean).join(' · ') || '—'}
          </div>
          <div style={rowMetaStyle(false)}>
            <span>
              {[discount, c.is_affiliate_or_flyer ? 'affiliate' : null, c.is_sponsored ? 'sponsored' : null]
                .filter(Boolean)
                .join(' · ') || '\u00a0'}
            </span>
            <span style={{ flex: 'none' }}>{timeAgo(c.published_at)}</span>
          </div>
        </SidebarRow>
      );
    });
  }

  return (
    <SidebarPanel
      title="Newest coupons"
      sub="coupons/filtered/ · published · newest first"
      expanded={expanded}
      cards
    >
      {body}
    </SidebarPanel>
  );
};

// ---------------------------------------------------------------------------
// SECTION: Sidebar tabs
// ---------------------------------------------------------------------------
const SIDEBAR_TABS = [
  { label: 'Coupons', value: 'coupons' },
  { label: 'Customers', value: 'customer' },
  { label: 'Businesses', value: 'business' },
  { label: 'Redemptions', value: 'redemptions' },
];

const USER_PANEL_TITLES = {
  customer: 'Newest customers',
  business: 'Newest businesses',
};

// Account-state flags worth an admin's attention at a glance.
const userTags = u =>
  [
    u.is_blocked ? 'blocked' : null,
    u.is_active === false ? 'inactive' : null,
    u.email_verified === false ? 'email unverified' : null,
    u.verified ? 'verified' : null, // businesses only
  ].filter(Boolean);

const userLines = (u, userType) => {
  if (userType === 'business') {
    return {
      primary: u.name || u.email,
      secondary: [u.contact_name, u.email, u.city].filter(Boolean).join(' · '),
    };
  }
  const fullName = [u.first_name, u.last_name].filter(Boolean).join(' ');
  return {
    primary: fullName || u.email,
    secondary: [fullName ? u.email : null, u.city].filter(Boolean).join(' · '),
  };
};

// ---------------------------------------------------------------------------
// Business card (expanded Businesses tab)
// TODO: header image / logo / extra field names are guesses; adjust to the
// real serializer fields.
// ---------------------------------------------------------------------------
const businessHeaderImage = u => u.header_image || u.header_image_url || u.cover_image || u.banner || null;
const businessLogoUrl = u => u.logo || u.logo_url || null;

const HEADER_GRADIENTS = [
  'linear-gradient(135deg, #ef4444, #7f1d1d)',
  'linear-gradient(135deg, #6f5fd0, #2d2566)',
  'linear-gradient(135deg, #1D9E75, #0b4a38)',
  'linear-gradient(135deg, #E0902B, #7a4410)',
];
const gradientFor = name => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return HEADER_GRADIENTS[Math.abs(hash) % HEADER_GRADIENTS.length];
};

const businessChips = u =>
  [
    u.verified ? { label: 'Verified', bg: '#E1F5EE', color: '#085041' } : null,
    u.is_blocked ? { label: 'Blocked', bg: '#FDE2E4', color: '#B91C1C' } : null,
    u.is_active === false ? { label: 'Inactive', bg: '#F1EFE8', color: '#444441' } : null,
    u.email_verified === false ? { label: 'Email unverified', bg: '#FAEEDA', color: '#633806' } : null,
  ].filter(Boolean);

const BusinessCard = ({ u }) => {
  const [headerFailed, setHeaderFailed] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [hover, setHover] = useState(false);

  const name = u.name || u.email || '—';
  const header = !headerFailed ? businessHeaderImage(u) : null;
  const logo = !logoFailed ? businessLogoUrl(u) : null;
  const initial = (name.trim()[0] || '?').toUpperCase();
  const chips = businessChips(u);
  const about = u.description || u.about || u.bio;
  const category = u.category && (typeof u.category === 'string' ? u.category : u.category.name);
  const contactLine = [u.contact_name, u.email].filter(Boolean).join(' · ');
  const reachLine = [u.phone || u.phone_number, u.website].filter(Boolean).join(' · ');

  return (
    <a
      href={u.id ? userUrl(u.id) : undefined}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        background: '#fff',
        border: '1px solid #e3e6ea',
        borderRadius: 12,
        overflow: 'hidden',
        color: 'inherit',
        textDecoration: 'none',
        boxShadow: hover ? '0 8px 22px rgba(0,0,0,0.12)' : '0 2px 6px rgba(0,0,0,0.05)',
        transform: hover ? 'translateY(-2px)' : 'none',
        transition: 'box-shadow 0.15s ease, transform 0.15s ease',
      }}
    >
      {/* header image (gradient fallback) + status chips */}
      <div style={{ position: 'relative', height: 130, background: gradientFor(name), flex: 'none' }}>
        {header && (
          <img
            src={header}
            alt=""
            onError={() => setHeaderFailed(true)}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        )}
        {chips.length > 0 && (
          <div
            style={{
              position: 'absolute',
              top: 10,
              right: 10,
              left: 10,
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
              gap: 6,
            }}
          >
            {chips.map(c => (
              <span
                key={c.label}
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '3px 10px',
                  borderRadius: 99,
                  background: c.bg,
                  color: c.color,
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                }}
              >
                {c.label}
              </span>
            ))}
          </div>
        )}
      </div>

      <div style={{ padding: '0 20px 16px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        {/* logo overlapping the header */}
        <div
          style={{
            width: 68,
            height: 68,
            marginTop: -34,
            borderRadius: '50%',
            border: '3px solid #fff',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            background: logo ? '#fff' : '#262626',
            color: '#fff',
            fontSize: 26,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            flex: 'none',
            position: 'relative',
          }}
        >
          {logo ? (
            <img
              src={logo}
              alt={name}
              onError={() => setLogoFailed(true)}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            initial
          )}
        </div>

        <div title={name} style={{ ...rowTitleStyle(true), fontSize: 19, marginTop: 10 }}>{name}</div>
        <div style={{ fontSize: 14, color: '#666', marginTop: 2 }}>
          {[u.city, category].filter(Boolean).join(' · ') || '—'}
        </div>

        {about && (
          <p
            style={{
              margin: '10px 0 0',
              fontSize: 14,
              lineHeight: 1.45,
              color: '#444',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {about}
          </p>
        )}

        <div style={{ marginTop: 10, fontSize: 14, color: '#555', lineHeight: 1.5, wordBreak: 'break-word' }}>
          {contactLine && <div>{contactLine}</div>}
          {reachLine && <div>{reachLine}</div>}
        </div>

        <div
          style={{
            marginTop: 'auto',
            paddingTop: 12,
            borderTop: '1px solid #f1f3f5',
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 13,
            color: '#777',
          }}
        >
          <span>{u.id ? `#${u.id}` : ''}</span>
          <span>Joined {timeAgo(u.signed_at)}</span>
        </div>
      </div>
    </a>
  );
};

const NewestUsersPanel = ({ userType, list, expanded }) => {
  const { data, fetching, error } = list || {};
  const rows = unwrap(data);
  const noun = userType === 'business' ? 'businesses' : 'customers';

  let body;
  if (rows.length === 0 && (fetching || !list)) {
    body = <SidebarMessage text="Loading…" />;
  } else if (rows.length === 0 && error) {
    body = <SidebarMessage text={`Could not load ${noun}`} color="#c0392b" />;
  } else if (rows.length === 0) {
    body = <SidebarMessage text="No data yet" />;
  } else if (expanded && userType === 'business') {
    body = rows.map(u => <BusinessCard key={u.id} u={u} />);
  } else {
    body = rows.map((u, i) => {
      const { primary, secondary } = userLines(u, userType);
      const tags = userTags(u);
      return (
        <SidebarRow key={u.id} href={u.id ? userUrl(u.id) : null} first={i === 0} expanded={expanded}>
          <div title={primary} style={rowTitleStyle(expanded)}>{primary}</div>
          <div title={secondary} style={rowSubStyle(expanded)}>{secondary || '—'}</div>
          <div style={rowMetaStyle(expanded)}>
            <span>{tags.join(' · ') || '\u00a0'}</span>
            <span style={{ flex: 'none' }}>
              {u.id ? `#${u.id} · ` : ''}
              {timeAgo(u.signed_at)}
            </span>
          </div>
        </SidebarRow>
      );
    });
  }

  return (
    <SidebarPanel
      title={USER_PANEL_TITLES[userType]}
      sub="users/admin/new-users/ · newest first"
      expanded={expanded}
      colMin={userType === 'business' ? 340 : undefined}
    >
      {body}
    </SidebarPanel>
  );
};

// ---------------------------------------------------------------------------
// Latest redemptions
// TODO: field names below are guesses. Adjust once the backend route exists.
// ---------------------------------------------------------------------------
const LatestRedemptionsPanel = ({ data, loading, error, expanded }) => {
  const all = unwrap(data);
  const rows = expanded ? all : all.slice(0, NEWEST_COUPONS_LIMIT);

  let body;
  if (rows.length === 0 && loading) {
    body = <SidebarMessage text="Loading…" />;
  } else if (rows.length === 0 && error) {
    body = <SidebarMessage text="Could not load redemptions" color="#c0392b" />;
  } else if (rows.length === 0) {
    body = <SidebarMessage text="No data yet" />;
  } else {
       body = rows.map((r, i) => {
      const couponId = r.coupon; // ApplyCoupon.coupon is a plain pk
      const title = r.coupon_title || (couponId ? `Coupon #${couponId}` : '—');
      const who =
        [r.user_name, r.user_email].filter(Boolean).join(' · ') ||
        (r.user_id ? `User #${r.user_id}` : '—');
      const businessName = r.business && r.business.name;
      const sub = [who, businessName].filter(Boolean).join(' · ');
      const state = typeof r.state === 'string' ? r.state.replace(/_/g, ' ').toLowerCase() : null;
      return (
        <SidebarRow key={r.id || i} href={couponId ? couponUrl(couponId) : null} first={i === 0} expanded={expanded}>
          <div title={title} style={rowTitleStyle(expanded)}>{title}</div>
          <div title={sub} style={rowSubStyle(expanded)}>{sub}</div>
          <div style={rowMetaStyle(expanded)}>
            <span>{state || '\u00a0'}</span>
            <span style={{ flex: 'none' }}>{timeAgo(r.created_at)}</span>
          </div>
        </SidebarRow>
      );
    });
  }

  return (
    <SidebarPanel title="Latest redemptions" sub="redemptions · newest first" expanded={expanded}>
      {body}
    </SidebarPanel>
  );
};

const AnalyticsDashboard = () => {
  const dispatch = useDispatch();

  const [days, setDays] = useState(14);

  // --- Redux state selectors ---
  const dailyStatsState     = useSelector(s => s.stats.dailyStats.byCoupon['all']);
  const positionBucketsState = useSelector(s => s.stats.positionBuckets.byCoupon['all']);
  // NOTE: assumes a `screenStats` slice exists in coupon-common's stats duck,
  // mirroring `positionBuckets` (same shape: byCoupon['all'] -> { data, fetching }).
  // If that slice/action doesn't exist yet, this section will stay empty/loading
  // until the duck is updated to match getPositionBuckets.
  const screenStatsState    = useSelector(s => s.stats.screenStats && s.stats.screenStats.byCoupon['all']);
  const rankingState        = useSelector(s => s.stats.ranking);
  const userDailyStatsState  = useSelector(s => s.stats.userDailyStats.byUser['all']);
  const userCouponStatsState = useSelector(s => s.stats.userCouponStats.byUser['all']);
  const rawEventsState       = useSelector(s => s.stats.userRawEvents);
   const newestCouponsState   = useSelector(s => s.coupons && s.coupons.newestCoupons) || {};
  const newUsersState = useSelector(s => s.admin && s.admin.userManager && s.admin.userManager.fetchNewUsers) || {};
  
    const newRedemptionsState = useSelector(s => s.latestRedemptions) || {};
  const [sidebarTab, setSidebarTab] = useState('coupons');
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const requestedUserTabs = useRef(new Set());

  useEffect(() => {
    if (sidebarTab === 'coupons') return;
    if (sidebarTab === 'redemptions') {
      // Refetch each time the tab opens: redemptions change often, unlike
      // the new-user lists, which are only requested once per tab.
      dispatch(redemptionActions.getLatestRedemptions());
      return;
    }
    if (requestedUserTabs.current.has(sidebarTab)) return;
    requestedUserTabs.current.add(sidebarTab);
    dispatch(fetchNewUsers.actions.getNewUsers(sidebarTab));
  }, [sidebarTab, dispatch]);

  // console.log('[dashboard] userDailyStats:', userDailyStatsState, 'rawEvents:', rawEventsState?.fetching, rawEventsState?.data?.length);

  // --- Fetchers ---
  const fetchCouponData = useCallback(
    days => {
      dispatch(actions.getDailyStats({ days }));
      dispatch(actions.getPositionBuckets({ days }));
      dispatch(actions.getScreenStats({ days }));
      dispatch(actions.getRanking({ days }));
    },
    [dispatch],
  );

  const fetchUserData = useCallback(
    days => {
      dispatch(actions.getUserDailyStats({ days }));
      dispatch(actions.getUserCouponStats({ days }));
      dispatch(actions.getUserRawEvents({ days: Math.min(days, 14) }));
    },
    [dispatch],
  );

  useEffect(() => { fetchCouponData(days); }, [days, fetchCouponData]);
  useEffect(() => { fetchUserData(days);   }, [days, fetchUserData]);
  useEffect(() => { dispatch(couponActions.getNewestCoupons()); }, [dispatch]);

  return (
    <AppWrapper
      showHeader
      showSideNav
      showHeadingRow
      headingText="📊 Analytics Dashboard"
    >
      <div className="col-md-12">
        <Breadcrumbs locationArray={['Admin', 'Analytics']} />

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'flex-start' }}>
        <div style={{ flex: '1 1 640px', minWidth: 0, display: sidebarExpanded ? 'none' : undefined }}>

        {/* COUPON STATS */}
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#aaa', marginBottom: 10, marginTop: 8 }}>
          Coupon stats
        </div>

        <TabRow tabs={DAY_TABS} active={days} onChange={setDays} />

        <AnalyticsOverview
  couponDays={days}
  userDays={days}
  dailyStats={dailyStatsState && dailyStatsState.data}
  positionBuckets={positionBucketsState && positionBucketsState.data}
  screenStats={screenStatsState && screenStatsState.data}
  ranking={rankingState.data}
  userDailyStats={userDailyStatsState && userDailyStatsState.data}
  loading={{
    coupon: !!(dailyStatsState && dailyStatsState.fetching),
    buckets: !!(positionBucketsState && positionBucketsState.fetching),
    screens: !!(screenStatsState && screenStatsState.fetching),
    ranking: !!rankingState.fetching,
    user: !!(userDailyStatsState && userDailyStatsState.fetching),
  }}
/>
<Divider />

        <CouponDailyStatsTable
          data={dailyStatsState && dailyStatsState.data}
          loading={!!(dailyStatsState && dailyStatsState.fetching)}
        />

        <PositionBucketsTable
          data={positionBucketsState && positionBucketsState.data}
          loading={!!(positionBucketsState && positionBucketsState.fetching)}
        />

        <ScreenBreakdownTable
          data={screenStatsState && screenStatsState.data}
          loading={!!(screenStatsState && screenStatsState.fetching)}
        />

        <RankingTable
          data={rankingState.data}
          loading={rankingState.fetching}
        />

        <Divider />

        {/* USER STATS */}
        <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#aaa', marginBottom: 10 }}>
          User stats
        </div>

        <UserDailyStatsTable
          data={userDailyStatsState && userDailyStatsState.data}
          loading={!!(userDailyStatsState && userDailyStatsState.fetching)}
        />

        <UserCouponStatsTable
          data={userCouponStatsState && userCouponStatsState.data}
          loading={!!(userCouponStatsState && userCouponStatsState.fetching)}
        />

        <RawEventsTable
          data={rawEventsState.data}
          loading={rawEventsState.fetching}
        />
        </div>

        <aside
          style={
            sidebarExpanded
              ? { flex: '1 1 100%', width: '100%', maxWidth: '100%' }
              : { flex: '0 0 auto', width: 320, maxWidth: '100%', position: 'sticky', top: 12 }
          }
        >
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <button
              onClick={() => setSidebarExpanded(v => !v)}
              title={sidebarExpanded ? 'Collapse sidebar' : 'Expand to full page'}
              style={{
                fontSize: 12,
                padding: '4px 10px',
                borderRadius: 4,
                border: '1px solid #dee2e6',
                background: '#fff',
                color: '#555',
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {sidebarExpanded ? '⤡ Collapse' : '⤢ Expand'}
            </button>
          </div>
          <TabRow tabs={SIDEBAR_TABS} active={sidebarTab} onChange={setSidebarTab} />
          {sidebarTab === 'coupons' ? (
            <NewestCouponsPanel
              data={newestCouponsState.data}
              loading={!!newestCouponsState.fetching}
              error={newestCouponsState.error}
              expanded={sidebarExpanded}
            />
          ) : sidebarTab === 'redemptions' ? (
            <LatestRedemptionsPanel
              data={newRedemptionsState.data}
              loading={!!newRedemptionsState.fetching}
              error={newRedemptionsState.error}
              expanded={sidebarExpanded}
            />
          ) : (
            <NewestUsersPanel userType={sidebarTab} list={newUsersState[sidebarTab]} expanded={sidebarExpanded} />
          )}
        </aside>
        </div>
      </div>
    </AppWrapper>
  );
};

export default AnalyticsDashboard;